/**
 * Axios instance pre-configured for the caster-manager BFF.
 *
 * - In dev (vite), `/api/*` is proxied to `http://localhost:8001` (see vite.config.ts).
 * - In prod, the SPA is served from the same origin as the API, so a relative
 *   baseURL works in both cases.
 * - JWT is read from localStorage on every request via an interceptor; expired
 *   / missing tokens trigger a global 401 handler that clears local state and
 *   bounces the user to /login.
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import { demoAdapter, isDemoMode } from "@/lib/demo";

export const TOKEN_STORAGE_KEY = "caster-manager.token";
export const USER_STORAGE_KEY = "caster-manager.user";

export const api: AxiosInstance = axios.create({
  baseURL: "/",
  timeout: 30_000,
  headers: { Accept: "application/json" },
  ...(isDemoMode() ? { adapter: demoAdapter } : {}),
});

if (isDemoMode()) {
  // eslint-disable-next-line no-console
  console.info("[caster-manager] demo mode active — any credentials accepted");
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  error: string;
  code: string;
}

export function extractApiError(err: unknown, fallback = "Unexpected error"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiError | undefined;
    if (data?.error) return data.error;
    if (err.message) return err.message;
  }
  return fallback;
}

/**
 * WebSocket origin matching the current page. Vite dev proxy is configured
 * with `ws: true`, so /api WS requests are forwarded to the BFF as well.
 */
export function wsBase(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
}
